import { useEffect, useRef } from "react";
import { userWsUrl } from "../lib/api";

/**
 * useUserHub — listens to the per-user WebSocket at /api/ws/me and forwards
 * typed events (inbox:new, dm:new, match:scheduled, match:result, mention,
 * league_chat:new) to a callback. Auto-reconnects with 1s → 15s backoff.
 *
 * Usage:
 *   useUserHub((evt) => {
 *     if (evt.type === "inbox:new") refreshInbox();
 *   });
 */
export function useUserHub(onEvent, enabled = true) {
  const wsRef = useRef(null);
  const onEventRef = useRef(onEvent);
  const backoffRef = useRef(1000);
  const closedRef = useRef(false);

  useEffect(() => { onEventRef.current = onEvent; }, [onEvent]);

  useEffect(() => {
    if (!enabled) return;
    closedRef.current = false;

    const connect = () => {
      if (closedRef.current) return;
      let ws;
      try {
        ws = new WebSocket(userWsUrl());
      } catch {
        scheduleReconnect();
        return;
      }
      wsRef.current = ws;
      ws.onopen = () => { backoffRef.current = 1000; };
      ws.onmessage = (e) => {
        try {
          const payload = JSON.parse(e.data);
          if (payload?.type === "pong" || payload?.type === "ready") return;
          onEventRef.current?.(payload);
        } catch { /* ignore */ }
      };
      ws.onerror = () => { /* close handler will reconnect */ };
      ws.onclose = () => { scheduleReconnect(); };
    };

    const scheduleReconnect = () => {
      if (closedRef.current) return;
      const delay = Math.min(15000, backoffRef.current);
      backoffRef.current = Math.min(15000, backoffRef.current * 2);
      setTimeout(connect, delay);
    };

    connect();

    // Lightweight client-side ping every 25s so the server-side keepalive
    // path stays warm and proxies don't terminate the socket.
    const pingId = setInterval(() => {
      try {
        if (wsRef.current?.readyState === 1) {
          wsRef.current.send(JSON.stringify({ type: "ping" }));
        }
      } catch { /* ignore */ }
    }, 25000);

    return () => {
      closedRef.current = true;
      clearInterval(pingId);
      try { wsRef.current?.close(); } catch { /* ignore */ }
    };
  }, [enabled]);
}
